/**
 * Group manage provider.
 *
 * Owns a scoped Zustand dialog store in context for permission-change UI state, while data is
 * accessed via hooks backed by Zero queries.
 */
import {
  createContext,
  useContext as useReactContext,
  useMemo,
  useRef,
  type PropsWithChildren,
} from "react";
import { useQuery, useZero } from "@corpuscomplete/zero-data-transformer/react";
import type { Zero, PromiseWithServerResult } from "@rocicorp/zero";
import { type StoreApi, createStore, useStore } from "zustand";
import { Space } from "@corpuscomplete/core/auth/space";
import { mutators, queries } from "@corpuscomplete/core/zero";
import { AuthUI } from "../..";
import { GroupUI } from "..";

export namespace GroupManage {
  export type DialogStore = {
    changePermissionsTarget: GroupUI.Member | null;
    newRootRole: GroupUI.RootRole;
    newMembersRole: GroupUI.MembersRole;
    openChangePermissions: (member: GroupUI.Member, grants: GroupUI.GrantLike[]) => void;
    closeChangePermissions: () => void;
    setNewRootRole: (role: GroupUI.RootRole) => void;
    setNewMembersRole: (role: GroupUI.MembersRole) => void;
  };

  function createDialogStore(): StoreApi<DialogStore> {
    return createStore<DialogStore>((set) => ({
      changePermissionsTarget: null,
      newRootRole: null,
      newMembersRole: null,
      openChangePermissions: (member, grants) =>
        set({
          changePermissionsTarget: member,
          newRootRole: GroupUI.getMemberRootRole(grants),
          newMembersRole: GroupUI.getMemberMembersRole(grants),
        }),
      closeChangePermissions: () =>
        set({
          changePermissionsTarget: null,
          newRootRole: null,
          newMembersRole: null,
        }),
      setNewRootRole: (role) => set({ newRootRole: role }),
      setNewMembersRole: (role) => set({ newMembersRole: role }),
    }));
  }

  export type Context = {
    groupId: string;
    userId: string;
  };

  const IdContext = createContext<Context | null>(null);
  const StoreContext = createContext<StoreApi<DialogStore> | null>(null);

  export type DataProviderProps = PropsWithChildren<{
    groupId: string;
    userId: string;
  }>;

  export function DataProvider({ children, groupId, userId }: DataProviderProps) {
    const dialogStoreRef = useRef<StoreApi<DialogStore> | null>(null);
    if (!dialogStoreRef.current) {
      dialogStoreRef.current = createDialogStore();
    }

    return (
      <IdContext value={{ groupId, userId }}>
        <StoreContext value={dialogStoreRef.current}>{children}</StoreContext>
      </IdContext>
    );
  }

  export function useContext(): Context {
    const context = useReactContext(IdContext);
    if (!context) {
      throw new Error("GroupManage.useContext must be used within GroupManage.DataProvider");
    }
    return context;
  }

  export function useDialogStore<T>(selector: (state: DialogStore) => T): T {
    const store = useReactContext(StoreContext);
    if (!store) {
      throw new Error("GroupManage.useDialogStore must be used within GroupManage.DataProvider");
    }
    return useStore(store, selector);
  }

  export function useGroup(): Space.Info | undefined {
    const { groupId } = useContext();
    const [group] = useQuery(queries.space.byId({ id: groupId }));
    return group;
  }

  export function usePermissions() {
    const { groupId } = useContext();
    return AuthUI.usePermissions(Space.Permissions, groupId);
  }

  export function useMyGroupGrants(): GroupUI.GrantLike[] {
    const { groupId } = useContext();
    const [allMyGrants] = useQuery(queries.accessGrant.myGrants());
    return useMemo(
      () => allMyGrants.filter((g) => g.resourceId === groupId && g.resourceType === "space"),
      [allMyGrants, groupId],
    );
  }

  export function useGrants(): {
    grantsByUserId: Map<string, GroupUI.GrantLike[]>;
    ownerCount: number;
  } {
    const { groupId } = useContext();
    const [memberGrantsData] = useQuery(queries.space.memberGrants({ groupId }));
    const grantsByUserId = useMemo(
      () => Space.buildGrantsByUserId(memberGrantsData),
      [memberGrantsData],
    );

    const group = useGroup();
    const members = group?.group?.members ?? [];
    const ownerCount = useMemo(() => {
      return members.filter(
        (m) => GroupUI.getMemberRootRole(grantsByUserId.get(m.userId) ?? []) === "owner",
      ).length;
    }, [members, grantsByUserId]);

    return { grantsByUserId, ownerCount };
  }

  export function useRoleMembers(): GroupUI.MemberWithGrants[] {
    const group = useGroup();
    const { grantsByUserId } = useGrants();
    const members = group?.group?.members ?? [];

    return useMemo(() => {
      const { withGrants } = GroupUI.partitionMembersByGrants(members, grantsByUserId);
      return withGrants;
    }, [members, grantsByUserId]);
  }

  export function useBasicMembers(): GroupUI.Member[] {
    const group = useGroup();
    const { grantsByUserId } = useGrants();
    const members = group?.group?.members ?? [];

    return useMemo(() => {
      const { withoutGrants } = GroupUI.partitionMembersByGrants(members, grantsByUserId);
      return withoutGrants;
    }, [members, grantsByUserId]);
  }

  export function useLoadingState(): { isLoading: boolean } {
    const { groupId } = useContext();
    const [, groupDetails] = useQuery(queries.space.byId({ id: groupId }));
    return {
      isLoading: groupDetails.type === "unknown",
    };
  }

  export type Mutations = {
    removeMember: (memberId: string) => PromiseWithServerResult;
    setRootGrant: (userId: string, role: GroupUI.RootRole) => PromiseWithServerResult;
    setMembersGrant: (userId: string, role: GroupUI.MembersRole) => PromiseWithServerResult;
  };

  export function useMutations(): Mutations {
    const z = useZero();
    const { groupId } = useContext();

    return useMemo(
      () => ({
        removeMember: (memberId) =>
          z.mutate(mutators.space.removeMember({ groupId, userId: memberId })),
        setRootGrant: (userId, role) =>
          z.mutate(mutators.space.setRootGrant({ groupId, userId, role })),
        setMembersGrant: (userId, role) =>
          z.mutate(mutators.space.setMembersGrant({ groupId, userId, role })),
      }),
      [z, groupId],
    );
  }

  export function preload(zero: Zero, groupId: string): void {
    void zero.run(queries.space.byId({ id: groupId }));
    void zero.run(queries.accessGrant.myGrants());
    void zero.run(queries.space.memberGrants({ groupId }));
  }
}
